import { useEffect, useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { getJeepRallyMediaUrl } from "../../../../../../api/jeep-rally/api";
import { toCategoryKey } from "../../utils/category.utils";

const CategoryForm = ({
  form,
  validationErrors,
  submitting,
  submitLabel = "Create Category",
  submittingLabel = "Creating...",
  isEditMode = false,
  readOnly = false,
  imageFile,
  existingImage,
  routes = [],
  isRoutesLoading = false,
  isRoutesError = false,
  onSubmit,
  onFieldChange,
  onImageChange,
  onBack,
}) => {
  const [keyTouched, setKeyTouched] = useState(isEditMode);
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    if (!imageFile) {
      setPreviewUrl("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(imageFile);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [imageFile]);

  const currentImageUrl = previewUrl || getJeepRallyMediaUrl(existingImage);

  const handleTitleChange = (value) => {
    onFieldChange("title", value);
    if (!isEditMode && !keyTouched) {
      onFieldChange("key", toCategoryKey(value));
    }
  };

  const handleKeyChange = (value) => {
    setKeyTouched(true);
    onFieldChange("key", value);
  };

  const handleImageChange = (file) => {
    onImageChange(file || null);
  };

  const disabled = submitting || readOnly;

  return (
    <Form onSubmit={onSubmit}>
      <h5 className="mb-3 mt-2">Basic Information</h5>
      <Row>
        {!isEditMode && (
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>
                Route <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={form.route_id}
                onChange={(e) => onFieldChange("route_id", e.target.value)}
                isInvalid={!!validationErrors.route_id}
                disabled={
                  disabled || isRoutesLoading || isRoutesError
                }
              >
                <option value="">
                  {isRoutesLoading ? "Loading routes..." : "Select a route"}
                </option>
                {routes.map((route) => {
                  const routeId = route._id || route.id;
                  const label =
                    route.title || route.name || `Route ${routeId}`;
                  return (
                    <option key={routeId} value={routeId}>
                      {label}
                    </option>
                  );
                })}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {validationErrors.route_id}
              </Form.Control.Feedback>
              {isRoutesError && (
                <Form.Text className="text-danger">
                  Failed to load routes. Please refresh and try again.
                </Form.Text>
              )}
            </Form.Group>
          </Col>
        )}

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={form.title}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Stock Prepaid"
              isInvalid={!!validationErrors.title}
              disabled={disabled}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.title}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Key <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={form.key}
              onChange={(e) => handleKeyChange(e.target.value)}
              placeholder="stock_prepaid"
              isInvalid={!!validationErrors.key}
              disabled={disabled}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.key}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Unique snake_case slug. Keep it stable — changing it can break
              related rallies, teams, and rankings.
            </Form.Text>
          </Form.Group>
        </Col>

        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.description}
              onChange={(e) => onFieldChange("description", e.target.value)}
              placeholder="Short description of this category"
              disabled={disabled}
            />
          </Form.Group>
        </Col>

        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Consent</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.consent}
              onChange={(e) => onFieldChange("consent", e.target.value)}
              placeholder="Optional consent text"
              disabled={disabled}
            />
          </Form.Group>
        </Col>
      </Row>

      <h5 className="mb-3 mt-2">Rules</h5>
      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>
              Max Members <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="1"
              value={form.max_members}
              onChange={(e) => onFieldChange("max_members", e.target.value)}
              isInvalid={!!validationErrors.max_members}
              disabled={disabled}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.max_members}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={4} className="d-flex align-items-center">
          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="navigator_allowed"
              label="Navigator allowed"
              checked={Boolean(form.navigator_allowed)}
              onChange={(e) =>
                onFieldChange("navigator_allowed", e.target.checked)
              }
              disabled={disabled}
            />
          </Form.Group>
        </Col>
      </Row>

      <h5 className="mb-3 mt-2">Image</h5>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Category Image</Form.Label>
            <Form.Control
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.gif,image/jpeg,image/png,image/webp,image/gif"
              onChange={(e) => handleImageChange(e.target.files?.[0] || null)}
              isInvalid={!!validationErrors.image}
              disabled={disabled}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.image}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Optional. JPEG, JPG, PNG, WEBP, or GIF. Max 50MB.
            </Form.Text>
          </Form.Group>
        </Col>

        <Col md={6}>
          {currentImageUrl ? (
            <img
              src={currentImageUrl}
              alt={form.title || "Category preview"}
              style={{
                width: "100%",
                maxWidth: "220px",
                height: "140px",
                objectFit: "cover",
                borderRadius: "8px",
                border: "1px solid #dee2e6",
              }}
            />
          ) : (
            <div
              className="d-flex align-items-center justify-content-center bg-light text-muted"
              style={{
                width: "220px",
                height: "140px",
                borderRadius: "8px",
                border: "1px solid #dee2e6",
              }}
            >
              No image
            </div>
          )}
        </Col>
      </Row>

      <div className="d-flex justify-content-end gap-2 mt-3">
        <Button
          type="button"
          variant="outline-secondary"
          onClick={onBack}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={disabled}>
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {submittingLabel}
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </Form>
  );
};

export default CategoryForm;
